import { Card, Typography } from 'antd';
import { COMPANY, DOC_DATES, SERVICE } from '@shared/legal';

const { Title, Paragraph, Text } = Typography;

/**
 * /legal/privacy — Политика конфиденциальности (10 апреля 2026 г.).
 *
 * Текст полностью соответствует исходному документу владельца, с тремя
 * правками для приведения в соответствие с реальной моделью сервиса:
 *  - email Оператора подставлен из COMPANY.email;
 *  - сайт регистрации в Соглашении (cabinet.richat.ru, не create.richat.ru);
 *  - LEGAL-OOO: трансграничная передача описана по факту (режимы
 *    локализации), добавлен раздел об обработке данных Конечных
 *    пользователей по поручению Пользователя;
 *  - убрана ошибочная ссылка раздела 8.1 на политику Tilda
 *    (в исходном документе ссылка была вставлена из шаблона).
 */

// Маленький хелпер для текста параграфа
const p = (children: React.ReactNode) => (
  <Paragraph style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 12 }}>
    {children}
  </Paragraph>
);

const li = (children: React.ReactNode) => (
  <li style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 4 }}>{children}</li>
);

export function LegalPrivacyPage() {
  return (
    <div style={{ maxWidth: 820, margin: '0 auto' }}>
      <Title level={2} style={{ marginTop: 0 }}>
        Политика конфиденциальности
      </Title>

      <Card style={{ marginBottom: 24 }}>
        <Text type="secondary">Оператор обработки персональных данных:</Text>
        <Paragraph style={{ margin: '6px 0 0', fontSize: 14 }}>
          {COMPANY.fullName}<br />
          ИНН {COMPANY.inn}<br />
          КПП {COMPANY.kpp}<br />
          ОГРН {COMPANY.ogrn}<br />
          {COMPANY.address}<br />
          E-mail:{' '}
          <a href={`mailto:${COMPANY.email}`} style={{ color: '#1F2937' }}>
            {COMPANY.email}
          </a>
        </Paragraph>
      </Card>

      {p(
        <>
          Настоящая политика конфиденциальности разработана в соответствии
          с п. 2 ч. 1 ст. 18.1 Федерального закона РФ от 27.07.2006
          № 152-ФЗ «О персональных данных» и действует в отношении персональных
          данных, которые могут быть получены оператором обработки персональных
          данных (далее — Оператор) при использовании сервиса {SERVICE.name} от
          его пользователей.
        </>,
      )}
      {p('Политика содержит информацию о том:')}
      <ul>
        {li('какие персональные данные Оператор получает от вас;')}
        {li('как Оператор использует и обрабатывает полученные персональные данные;')}
        {li('как обеспечивается конфиденциальность и безопасность персональных данных.')}
      </ul>

      <Title level={3} style={{ marginTop: 32, fontWeight: 500 }}>
        1. Термины и определения
      </Title>
      {p(
        <>
          <Text strong>Персональные данные (ПДн)</Text> — любая информация,
          относящаяся прямо или косвенно к определённому или определяемому
          физическому лицу (субъекту ПДн).
        </>,
      )}
      {p(
        <>
          <Text strong>Обработка персональных данных</Text> — действие/совокупность
          действий, совершаемых с использованием средств автоматизации либо без
          использования таких средств, с ПДн, включая: сбор, запись,
          систематизацию, накопление, хранение, уточнение, извлечение,
          использование, передачу (распространение, предоставление, доступ),
          обезличивание, блокирование, удаление, уничтожение ПДн.
        </>,
      )}
      {p(
        <>
          <Text strong>Автоматизированная обработка персональных данных</Text> —
          обработка ПДн с использованием компьютерной техники и алгоритмов,
          включая механизмы искусственного интеллекта.
        </>,
      )}
      {p(
        <>
          <Text strong>Обезличенные персональные данные</Text> — данные,
          полученные в результате обработки ПДн или Загружаемых данных
          Пользователя, которые не позволяют идентифицировать конкретное
          физическое лицо, включая:
        </>,
      )}
      <ul>
        {li('лог-файлы и метаданные;')}
        {li('статистическую информацию о взаимодействии с Сервисом.')}
      </ul>
      {p(
        <>
          <Text strong>Опубликование Политики</Text> — размещение Оператором
          актуальной версии Политики в интерфейсе Сервиса.
        </>,
      )}
      {p(
        <>
          <Text strong>Пользователь</Text> — субъект персональных данных; лицо,
          имеющее доступ к Сервису и его функциям.
        </>,
      )}
      {p(
        <>
          <Text strong>Аккаунт</Text> — персональная учётная запись Пользователя
          в Сервисе, содержащая сведения, необходимые для идентификации
          Пользователя, предоставления доступа к функционалу Сервиса и учёта
          использования Сервиса.
        </>,
      )}
      {p(
        <>
          <Text strong>Пользовательское соглашение</Text> — публичная оферта,
          регулирующая отношения между Оператором (владельцем Сервиса) и
          Пользователем. Устанавливает правила пользования Сервисом, права,
          обязанности и ответственность сторон в процессе их взаимоотношений в
          связи с использованием Сервиса.
        </>,
      )}
      {p(
        <>
          <Text strong>Сервис</Text> — программное обеспечение «{SERVICE.fullName}»
          (реестровая запись {SERVICE.registryRecord} в {SERVICE.registryName}),
          доступ к которому предоставляется на сайте {SERVICE.publicSite}.
        </>,
      )}
      {p(
        <>
          <Text strong>Загружаемые данные</Text> — данные, которые Пользователь
          передаёт в Сервис путём их загрузки через интерфейс Сервиса.
        </>,
      )}
      {p(
        <>
          <Text strong>Ассистент</Text> — создаваемый Пользователем ИИ-ассистент,
          взаимодействующий с третьими лицами в каналах, подключённых
          Пользователем.
        </>,
      )}
      {p(
        <>
          <Text strong>Конечный пользователь</Text> — физическое лицо,
          взаимодействующее с Ассистентом Пользователя в подключённых каналах
          (виджет на сайте, мессенджеры), не являющееся Пользователем Сервиса.
        </>,
      )}
      {p(
        <>
          <Text strong>Режим локализации</Text> — выбираемый Пользователем при
          создании Ассистента режим, определяющий, какие технологические сервисы
          используются для обработки данных и на территории какого государства
          она осуществляется.
        </>,
      )}
      {p(
        <>
          <Text strong>Лог-файлы</Text> — автоматически создаваемые Сервисом
          записи, фиксирующие хронологическую последовательность действий
          Пользователя, событий или процессов, происходящих в рамках
          взаимодействия Пользователя и Сервиса.
        </>,
      )}
      {p(
        <>
          <Text strong>Cookie-файлы</Text> — небольшие текстовые файлы,
          отправляемые веб-сервером и сохраняемые на устройстве пользователя
          (компьютере, смартфоне) браузером.
        </>,
      )}

      <Title level={3} style={{ marginTop: 32, fontWeight: 500 }}>
        2. Общие положения
      </Title>
      {p('2.1. Настоящая политика конфиденциальности разработана в соответствии с п. 2 ч. 1 ст. 18.1 Федерального закона РФ от 27.07.2006 № 152-ФЗ «О персональных данных» (далее — Закон о персональных данных) и действует в отношении ПДн, которые могут быть получены Оператором от Пользователя при использовании Сервиса.')}
      {p('2.2. Оператор обрабатывает персональные данные следующих категорий субъектов персональных данных:')}
      <ul>
        {li('Пользователей Сервиса — физических лиц;')}
        {li('Пользователей Сервиса — индивидуальных предпринимателей;')}
        {li('представителей юридических лиц и индивидуальных предпринимателей, использующих Сервис;')}
        {li('Конечных пользователей — в объёме и на условиях, установленных разделом 4 настоящей Политики (обработка по поручению Пользователя).')}
      </ul>
      {p('2.3. Оператор осуществляет обработку персональных данных на законных основаниях, предусмотренных законодательством Российской Федерации, включая согласие субъекта персональных данных, исполнение договора с Пользователем, а также исполнение обязанностей, возложенных на Оператора законодательством.')}
      {p('2.4. Оператор обрабатывает только те данные Пользователя и только теми способами, которые перечислены в Политике и отвечают целям их обработки. Содержание и объём обрабатываемых данных соответствует заявленным целям обработки, избыточность обрабатываемых данных не допускается.')}
      {p('2.5. Пользователь самостоятельно несёт ответственность за законность передачи в Сервис персональных данных третьих лиц, включая наличие необходимых согласий и иных правовых оснований. Оператор не обязан проверять наличие таких оснований, если иное не предусмотрено законодательством.')}
      {p('2.6. Оператор не осуществляет обработку специальных категорий персональных данных, предусмотренных ст. 10 Закона о персональных данных, а также не осуществляет биометрическую идентификацию Пользователей.')}
      {p('2.7. Запись, систематизация, накопление, хранение, уточнение и извлечение персональных данных граждан Российской Федерации осуществляются с использованием баз данных, находящихся на территории Российской Федерации. Трансграничная передача персональных данных Оператором не осуществляется, за исключением случая, предусмотренного пунктом 2.9 настоящей Политики.')}
      {p('2.8. Правовыми основаниями обработки ПДн являются:')}
      <ul>
        {li(<>Согласие субъекта персональных данных на обработку его ПДн, выраженное в электронной форме путём акцепта Политики при посещении сайта {SERVICE.publicSite};</>)}
        {li('Исполнение Оператором своих обязательств в соответствии с условиями Пользовательского соглашения (ст. 6 п. 1 пп. 5 Закона о персональных данных).')}
      </ul>
      {p('2.9. Режимы локализации и территория обработки. При создании Ассистента Пользователь самостоятельно выбирает Режим локализации:')}
      <ul>
        {li('«Только российские сервисы» — обработка данных Ассистента осуществляется исключительно с использованием технологических сервисов, расположенных на территории Российской Федерации. Данные за пределы Российской Федерации не передаются;')}
        {li('«Российские сервисы, кроме изображений» — обработка текстовых данных осуществляется на территории Российской Федерации; распознавание изображений, содержащихся в Загружаемых данных, осуществляется с привлечением технологического партнёра, расположенного за пределами Российской Федерации;')}
        {li('«Международные сервисы» — обработка данных Ассистента осуществляется с привлечением технологических партнёров, расположенных за пределами Российской Федерации.')}
      </ul>
      {p('Выбирая режим, предполагающий привлечение технологических партнёров за пределами Российской Федерации, Пользователь даёт согласие на трансграничную передачу данных, направляемых в соответствующего Ассистента, и подтверждает наличие правовых оснований для такой передачи в отношении персональных данных третьих лиц. Режим локализации отображается в интерфейсе Сервиса и не может быть изменён после создания Ассистента.')}

      <Title level={3} style={{ marginTop: 32, fontWeight: 500 }}>
        3. Данные и цели обработки
      </Title>
      {p('3.1. Оператор осуществляет автоматизированную обработку персональных данных Пользователей в объёме, необходимом для достижения целей, указанных в настоящей Политике, включая сбор, запись, систематизацию, хранение, уточнение (обновление, изменение), извлечение, использование, передачу (предоставление доступа уполномоченным лицам и подрядчикам), блокирование, удаление и уничтожение персональных данных.')}
      {p('3.2. Цели обработки ПДн Пользователей:')}
      <ul>
        {li('Исполнение Оператором обязательств, вытекающих из Пользовательского соглашения, в т.ч.: регистрации и идентификации Пользователя; предоставления доступа к Сервису; обеспечения расчётов и документооборота; направления сервисных уведомлений.')}
        {li('Обеспечение технической поддержки, корректной и безопасной работы Сервиса.')}
        {li('Исполнение обязанностей, возложенных на Оператора законодательством Российской Федерации, включая налоговое законодательство и законодательство о бухгалтерском учёте.')}
      </ul>
      {p('3.3. Оператор не обрабатывает избыточные или несовместимые ПДн по отношению к вышеуказанным целям.')}
      {p('3.4. Оператор обрабатывает только те персональные данные, которые являются необходимыми и достаточными для достижения целей обработки. Сбор избыточных данных запрещён.')}
      {p('3.5. Запись, систематизация, накопление, хранение, уточнение и извлечение персональных данных граждан Российской Федерации осуществляются с использованием баз данных, находящихся на территории Российской Федерации.')}
      {p('3.6. В рамках работы с Сервисом Пользователь для целей, перечисленных в п. 3.2 Политики, может предоставлять следующие ПДн:')}

      <Card styles={{ body: { padding: 0 } }} style={{ marginBottom: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              <th style={th}>№</th>
              <th style={th}>Цель обработки</th>
              <th style={th}>Персональные данные</th>
              <th style={th}>Срок обработки / хранения</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={td}>1</td>
              <td style={td}>
                Исполнение Оператором обязательств, вытекающих из Пользовательского
                соглашения, в т.ч. предоставление Пользователю доступа к
                функциональности Сервиса
              </td>
              <td style={td}>
                адрес электронной почты; телефон; фамилия, имя, отчество*;
                ИНН*; ОГРНИП*; наименование и реквизиты организации*;
                банковские реквизиты*
              </td>
              <td style={td}>в течение срока жизни Аккаунта</td>
            </tr>
            <tr>
              <td style={td}>2</td>
              <td style={td}>Обеспечение корректной и безопасной работы Сервиса</td>
              <td style={td}>лог-файлы</td>
              <td style={td}>до 1 года</td>
            </tr>
            <tr>
              <td style={td}>3</td>
              <td style={td}>Обеспечение функционирования Сервиса</td>
              <td style={td}>файлы cookie (подробнее см. раздел 5)</td>
              <td style={td}>
                в соответствии со сроками хранения, установленными браузером
                Пользователя и политиками соответствующих поставщиков аналитики,
                но не более сроков, указанных в разделе 5 настоящей Политики
              </td>
            </tr>
            <tr>
              <td style={td}>4</td>
              <td style={td}>
                Исполнение требований налогового законодательства и
                законодательства о бухгалтерском учёте
              </td>
              <td style={td}>
                сведения о расчётах, платёжные и бухгалтерские документы
              </td>
              <td style={td}>
                не менее 5 лет с года, в котором документ использовался
                в последний раз
              </td>
            </tr>
          </tbody>
        </table>
      </Card>
      <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 16 }}>
        * Только для Пользователей — индивидуальных предпринимателей и
        представителей юридических лиц, в объёме, необходимом для расчётов
        и документооборота.
      </Paragraph>

      {p('3.7. По истечении срока обработки ПДн, указанных в п. 3.2 Политики, ПДн подлежат удалению или уничтожению иным способом. ПДн, указанные в пп. 1–2 п. 3.6 Политики, удаляются автоматически, если более длительное хранение не требуется в силу законодательства Российской Федерации.')}
      {p('Уничтожение персональных данных осуществляется путём удаления из информационных систем Оператора, уничтожения материальных носителей либо иным способом, исключающим возможность восстановления персональных данных, в сроки, установленные законодательством Российской Федерации и внутренними регламентами Оператора.')}
      {p('3.8. Оператор обрабатывает ПДн Пользователя только в случае их заполнения и/или отправки Пользователем самостоятельно через специальные формы Сервиса.')}
      {p('3.9. Оператор вправе поручать обработку персональных данных третьим лицам (в том числе хостинг-провайдерам, поставщикам облачной инфраструктуры, сервисам аналитики, подрядчикам технической поддержки) исключительно в объёме, необходимом для достижения целей обработки, при условии соблюдения такими лицами требований законодательства Российской Федерации о персональных данных.')}
      {p('3.10. Согласие Пользователя на обработку персональных данных считается предоставленным с момента проставления отметки («галочки») в соответствующем поле интерфейса Сервиса при заполнении формы, предусматривающей предоставление персональных данных.')}
      {p('3.11. Заполняя формы, предусмотренные Сервисом, Пользователь подтверждает, что:')}
      <ul>
        {li('прочитал и ознакомился с Политикой в полном объёме;')}
        {li('содержание и текст Политики ему понятны и ясны;')}
        {li('он обладает полной дееспособностью;')}
        {li('его согласие выражено в добровольном порядке, своей волей и в своём интересе.')}
      </ul>

      <Title level={3} style={{ marginTop: 32, fontWeight: 500 }}>
        4. Обработка данных по поручению Пользователя
      </Title>
      {p('4.1. Используя функционал Сервиса, Пользователь самостоятельно загружает в Сервис Загружаемые данные и подключает каналы взаимодействия Ассистентов с Конечными пользователями. В составе таких данных и переписок Конечных пользователей с Ассистентами могут содержаться персональные данные третьих лиц.')}
      {p('4.2. В отношении персональных данных, содержащихся в Загружаемых данных и в переписках Конечных пользователей с Ассистентами, оператором является Пользователь. Оператор Сервиса осуществляет обработку таких данных по поручению Пользователя в соответствии с ч. 3 ст. 6 Закона о персональных данных, исключительно в объёме и в целях, необходимых для функционирования Сервиса.')}
      {p('4.3. Обработка по поручению включает: хранение, систематизацию, индексацию, извлечение, передачу в технологические сервисы, необходимые для формирования ответов Ассистента, отображение в интерфейсе Сервиса, резервное копирование и удаление.')}
      {p('4.4. Оператор не использует данные, обрабатываемые по поручению Пользователя, в собственных целях, не передаёт их третьим лицам за пределами целей, указанных в пункте 4.3, и не осуществляет их обработку после прекращения использования Сервиса Пользователем, за исключением случаев, когда хранение необходимо в силу требований законодательства, обеспечения информационной безопасности либо разрешения споров.')}
      {p('4.5. Пользователь как оператор персональных данных Конечных пользователей самостоятельно обеспечивает:')}
      <ul>
        {li('наличие правовых оснований обработки, включая получение согласий субъектов;')}
        {li('информирование субъектов персональных данных об осуществляемой обработке;')}
        {li('рассмотрение обращений субъектов персональных данных;')}
        {li('правомерность состава и объёма данных, передаваемых в Сервис.')}
      </ul>
      {p('4.6. Оператор оказывает Пользователю разумное содействие в исполнении обязанностей, предусмотренных пунктом 4.5, в пределах технических возможностей Сервиса, включая предоставление возможности удаления Загружаемых данных и переписок через интерфейс Сервиса.')}
      {p('4.7. Срок обработки данных, указанных в настоящем разделе, определяется сроком использования Пользователем Сервиса. Данные удаляются при удалении соответствующего Ассистента, Загружаемых данных либо Аккаунта Пользователя в порядке, предусмотренном Пользовательским соглашением.')}

      <Title level={3} style={{ marginTop: 32, fontWeight: 500 }}>
        5. Файлы cookie
      </Title>
      {p(
        <>
          4.1. Сервис может использовать обязательные технические cookie-файлы,
          необходимые для корректной работы Сервиса, а также cookie сторонних
          поставщиков аналитики.
        </>,
      )}
      {p(
        <>
          К числу сторонних поставщиков относятся cookie Яндекс.Метрики
          (подробнее см.{' '}
          <a
            href="https://yandex.ru/support/metrica/ru/general/cookie-usage"
            target="_blank"
            rel="noreferrer"
            style={{ color: '#1F2937' }}
          >
            https://yandex.ru/support/metrica/ru/general/cookie-usage
          </a>
          ).
        </>,
      )}
      {p('На Сервисе собираются следующие cookie*:')}

      <Card styles={{ body: { padding: 0 } }} style={{ marginBottom: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              <th style={th}>Поставщик</th>
              <th style={th}>Название</th>
              <th style={th}>Описание</th>
              <th style={th}>Срок хранения</th>
              <th style={th}>Тип</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Яндекс.Метрика', '_ym_d', 'Хранит дату первого визита посетителя на сайт', '1 год', 'Статистические'],
              ['Яндекс.Метрика', '_ym_isad', 'Используется для определения наличия у посетителя блокировщиков рекламы', '2 дня', 'Статистические'],
              ['Яндекс.Метрика', '_ym_uid', 'Используется для корректной работы Вебвизора', '1 год', 'Статистические'],
              ['Яндекс.Метрика', '_ym_visorc', 'Используется для корректной работы Вебвизора', '30 минут', 'Статистические'],
              ['Яндекс.Метрика', 'bh', 'Используется для отслеживания поведения пользователей', '1 год', 'Статистические'],
              ['Яндекс.Метрика', 'i', 'Позволяет различать посетителей', '1 год', 'Статистические'],
              ['Яндекс.Метрика', 'yabs-sid', 'Идентификатор визита', 'В течение сессии браузера', 'Статистические'],
              ['Яндекс.Метрика', 'yandexuid', 'Позволяет различать посетителей', '1 год', 'Статистические'],
              ['Яндекс.Метрика', 'yashr', 'Используется для отслеживания взаимодействий с рекламой', '1 год', 'Статистические'],
              ['Яндекс.Метрика', 'ymex', 'Хранит вспомогательную информацию для работы Метрики', '1 год', 'Статистические'],
              ['Яндекс.Метрика', 'yuidss', 'Позволяет различать посетителей', '1 год', 'Статистические'],
            ].map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j} style={td}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 16 }}>
        * Сторонний поставщик может расширить или изменить состав собираемых
        cookie. Оператор не может это контролировать и не несёт за это
        ответственность.
      </Paragraph>

      {p('5.2. Отключение технических cookie-файлов в настройках браузера Пользователя может привести к ограничению доступа к отдельным функциям Сервиса либо к некорректной работе Сервиса.')}
      {p('5.3. Использование необязательных аналитических cookie-файлов осуществляется при наличии согласия Пользователя, выраженного через интерфейс Сервиса либо настройки браузера.')}
      {p('5.4. При первом посещении Сервиса Пользователь вправе выразить согласие на использование необязательных cookie-файлов либо отказаться от их использования. Для отзыва ранее предоставленного согласия пользователю необходимо внести изменения в настройки используемого им браузера.')}

      <Title level={3} style={{ marginTop: 32, fontWeight: 500 }}>
        6. Основные права и обязанности Оператора и Пользователя
      </Title>
      {p('6.1. Оператор имеет право:')}
      <ul>
        {li('самостоятельно определять состав и перечень мер, необходимых и достаточных для обеспечения выполнения обязанностей, предусмотренных Законом о персональных данных и принятыми в соответствии с ним нормативными правовыми актами, если иное не предусмотрено Законом о персональных данных или другими федеральными законами;')}
        {li('после истечения срока действия согласия продолжать обработку ПДн в той части, в которой для её осуществления согласие не требуется или не будет требоваться в силу действующего законодательства (п. 2 ст. 9 Закона о персональных данных).')}
      </ul>
      {p('6.2. Оператор обязан:')}
      <ul>
        {li('организовывать обработку ПДн в соответствии с требованиями Закона о персональных данных и настоящей Политикой;')}
        {li('предоставлять Пользователю по его запросу информацию об обработке ПДн Пользователя;')}
        {li('использовать полученные ПДн исключительно для целей, указанных в Политике;')}
        {li('соблюдать конфиденциальность ПДн, не раскрывать третьим лицам и не распространять персональные данные без согласия Пользователя;')}
        {li('принимать правовые, организационные и технические меры по обеспечению конфиденциальности и безопасности ПДн Пользователя согласно порядку, обычно используемому для защиты такого рода информации в существующем деловом обороте;')}
        {li('прекратить обработку ПДн и уничтожить их в случае обращения Пользователя с соответствующим требованием в срок, не превышающий десяти рабочих дней с даты получения соответствующего требования.')}
      </ul>
      {p('6.3. Пользователь вправе:')}
      <ul>
        {li('получать доступ к своим ПДн, за исключением случаев, когда такой доступ ограничивается в соответствии с требованиями законодательства или невозможен;')}
        {li('получать достоверную информацию об обработке ПДн, в том числе подтверждение факта обработки, о целях и способах обработки, сроках обработки и пр.;')}
        {li('требовать от Оператора уточнения его ПДн, их блокирования или уничтожения в случае, если ПДн являются неполными, устаревшими, неточными, незаконно полученными или не являются необходимыми для заявленной цели обработки, а также принимать предусмотренные законом меры по защите своих прав;')}
        {li('отозвать согласие на обработку ПДн;')}
        {li('обжаловать в Роскомнадзоре или в судебном порядке неправомерные действия или бездействие Оператора при обработке его ПДн.')}
      </ul>
      {p('6.4. Для отзыва согласия на обработку персональных данных, направления запроса о предоставлении информации, уточнении, блокировании или удалении персональных данных Пользователь вправе направить обращение на адрес электронной почты Оператора, указанный в настоящей Политике.')}
      {p('6.5. Отзыв согласия на обработку персональных данных не влияет на законность обработки, осуществлённой до момента получения Оператором такого отзыва, а также не препятствует обработке персональных данных в случаях, когда такая обработка допускается без согласия в силу закона.')}

      <Title level={3} style={{ marginTop: 32, fontWeight: 500 }}>
        7. Конфиденциальность персональных данных, обеспечение их защиты и безопасности
      </Title>
      {p('7.1. Оператор обязуется обеспечивать конфиденциальность ПДн Пользователя, т.е. не распространять и не раскрывать третьим лицам его ПДн за исключением случаев, связанных с исполнением действующего законодательства.')}
      {p('7.2. Полученные ПДн являются конфиденциальными, защищены от любого потенциального неправомерного использования и используются только для целей, указанных в настоящей Политике.')}
      {p('7.3. Оператор принимает все необходимые меры по технологической и операционной безопасности ПДн Пользователей от потенциального неправомерного использования, несанкционированного доступа, раскрытия, искажения, блокирования или уничтожения.')}
      {p('7.4. К мерам, применяемым Оператором, относятся:')}
      <ul>
        {li('разработка документов, регулирующих отношения Оператора и Пользователя в сфере обработки и защиты ПДн;')}
        {li('назначение ответственного лица за организацию обработки персональных данных;')}
        {li('применение правовых, технических и организационных мер по обеспечению безопасности ПДн Пользователей;')}
        {li('оценка вреда, который может быть причинён субъекту ПДн в случае нарушения законодательства о персональных данных;')}
        {li('установление правил доступа к ПДн Пользователей;')}
        {li('разграничение прав доступа сотрудников Оператора к информационным системам;')}
        {li('ведение учёта носителей и резервных копий.')}
      </ul>
      {p('7.5. Если становится известно об утечке ПДн, Оператор:')}
      <ul>
        {li('в течение 24 часов уведомляет об этом Роскомнадзор;')}
        {li('в течение 72 часов проводит собственное расследование и уведомляет о его результатах Роскомнадзор.')}
      </ul>

      <Title level={3} style={{ marginTop: 32, fontWeight: 500 }}>
        8. Ограничения Политики
      </Title>
      {p('8.1. Политика применима только к Сервису и не распространяется на другие сторонние сервисы, в т.ч. веб-сайты, мобильные приложения, мессенджеры и пр., в т.ч. в случаях, когда:')}
      <ul>
        {li('сторонний сервис содержит ссылку на Сервис;')}
        {li('Сервис содержит ссылку на сторонний сервис.')}
      </ul>
      {p('8.2. Порядок обработки ПДн на сторонних сервисах производится в соответствии с их собственными правилами конфиденциальности, которые могут отличаться от данной Политики.')}

      <Title level={3} style={{ marginTop: 32, fontWeight: 500 }}>
        9. Заключительные положения
      </Title>
      {p('9.1. Политика вступает в силу с момента её опубликования Оператором и действует бессрочно. Дата публикации указана в Политике.')}
      {p('9.2. Оператор проводит пересмотр положений Политики и их актуализацию по мере необходимости, а также в следующих случаях:')}
      <ul>
        {li('при изменении положений законодательства РФ в области ПДн;')}
        {li('при создании новых или внесении изменений в существующие процессы обработки ПДн.')}
      </ul>
      {p('При внесении изменений в актуальной редакции Политики указывается дата последнего обновления. Новая редакция Политики вступает в силу с момента её опубликования.')}
      {p('9.3. К отношениям, связанным с обработкой персональных данных в рамках настоящей Политики, применяется законодательство Российской Федерации.')}
      {p('Все споры и разногласия, связанные с обработкой персональных данных, разрешаются в соответствии с законодательством Российской Федерации. Если Пользователь является физическим лицом, применяются правила подсудности, установленные законодательством Российской Федерации.')}
      {p('9.4. Пользователь может получить любые разъяснения по любым вопросам, связанным с исполнением или изменением Политики, а также обработкой персональных данных, по указанному в Политике адресу электронной почты.')}
      {p('Оператор предоставляет информацию по запросу, касающемуся обработки ПДн в соответствии с Политикой, в течение десяти рабочих дней с даты получения запроса Пользователя.')}
      {p('9.5. Настоящая редакция Политики заменяет собой редакцию, действовавшую до даты её размещения. Обработка персональных данных, начатая до вступления настоящей редакции в силу, продолжается Оператором на условиях настоящей Политики.')}

      <Paragraph
        type="secondary"
        style={{ marginTop: 32, fontSize: 13, textAlign: 'right' }}
      >
        {DOC_DATES.privacyLastUpdated}
      </Paragraph>
    </div>
  );
}

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  borderBottom: '1px solid #E5E7EB',
  fontWeight: 500,
  color: '#6B7280',
  fontSize: 12,
};

const td: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: '1px solid #F3F4F6',
  verticalAlign: 'top',
  color: '#1A1A1A',
};
